// Feature configuration for the dashboard template
// This allows easy customization of available features per project

export interface FeatureConfig {
  authentication: boolean;
  dashboard: boolean;
  analytics: boolean;
  settings: boolean;
  notifications: boolean;
  reports: boolean;
  payments: boolean;
  
  // Advanced Admin Features (New)
  admin_dashboard: boolean;
  admin_users: boolean;
  admin_bookings: boolean;
  admin_management: boolean;
  audit_logs: boolean;
  system: boolean;
  
  // Hotel Booking Platform Features
  hotels: boolean;
  bookings: boolean;
  users: boolean;
  user_registration: boolean;
  user_management: boolean;
  reviews: boolean;
  support: boolean;
  
  // Property Management Features
  property_types: boolean;
  
  // Legacy Hotel Management Features
  rooms: boolean;
  guests: boolean;
  housekeeping: boolean;
  maintenance: boolean;
  staff: boolean;
  
  // Legacy features (kept for compatibility)
  drivers: boolean;
  riders: boolean;
  trips: boolean;
  vehicles: boolean;
  complaints: boolean;
  cities: boolean;
  
  // Content Management Features
  static_content: boolean;
  
  // Profile Management Features
  profile_settings: boolean;
  
  // Promotions & Coupons Features
  coupons: boolean;
  
  // E-commerce Features
  products: boolean;
  categories: boolean;
  brands: boolean;
  orders: boolean;
  
  // UI Features
  darkMode: boolean;
  multiLanguage: boolean;
  realTimeUpdates: boolean;
  mobileResponsive: boolean;
}

export const defaultFeatures: FeatureConfig = {
  authentication: true,
  dashboard: true,
  analytics: true,
  settings: true,
  notifications: true,
  reports: true,
  payments: true,
  
  // Advanced Admin Features (New) - Disabled problematic admin features
  admin_dashboard: false,
  admin_users: false,
  admin_bookings: false,
  admin_management: false,
  audit_logs: false,
  system: false,
  
  // Hotel Booking Platform Features - Disabled for e-commerce
  hotels: false,
  bookings: false,
  users: false,
  user_registration: false,
  user_management: true,
  reviews: false,
  support: false,
  
  // Property Management Features - Disabled for e-commerce
  property_types: false,
  
  // Legacy Hotel Management Features - Disabled for e-commerce
  rooms: false,
  guests: false,
  housekeeping: false,
  maintenance: false,
  staff: false,
  
  // Legacy features
  drivers: false,
  riders: false,
  trips: false,
  vehicles: false,
  complaints: false,
  cities: false,
  
  // Content Management Features
  static_content: false,
  
  // Profile Management Features
  profile_settings: true,
  
  // Promotions & Coupons Features
  coupons: false,
  
  // E-commerce Features - Enabled
  products: true,
  categories: true,
  brands: true,
  orders: true,
  
  // UI Features
  darkMode: true,
  multiLanguage: true,
  realTimeUpdates: true,
  mobileResponsive: true,
};

export const hotelBookingFeatures: FeatureConfig = {
  ...defaultFeatures,
  hotels: false,
  bookings: true,
  users: true,
  user_registration: true,
  reviews: true,
  support: true,
  analytics: true,
  reports: true,
};

export const hotelFeatures: FeatureConfig = {
  ...defaultFeatures,
  rooms: true,
  bookings: true,
  guests: true,
  housekeeping: true,
  maintenance: true,
  staff: true,
  analytics: true,
  reports: true,
};

export const restaurantFeatures: FeatureConfig = {
  ...defaultFeatures,
  rooms: false,
  bookings: false,
  guests: false,
  housekeeping: false,
  maintenance: false,
  staff: true,
  analytics: true,
  reports: true,
};

export const ecommerceFeatures: FeatureConfig = {
  ...defaultFeatures,
  rooms: false,
  bookings: false,
  guests: false,
  housekeeping: false,
  maintenance: false,
  staff: false,
  users: true,
  user_registration: true,
  analytics: true,
  reports: true,
};

export const minimalFeatures: FeatureConfig = {
  authentication: true,
  dashboard: true,
  analytics: false,
  settings: true,
  notifications: false,
  reports: false,
  
  // Advanced Admin Features (disabled in minimal)
  admin_dashboard: false,
  admin_users: false,
  admin_bookings: false,
  admin_management: false,
  audit_logs: false,
  system: false,
  
  // Hotel Booking Platform Features
  hotels: false,
  bookings: true,
  users: true,
  user_registration: true,
  user_management: false,
  reviews: false,
  support: false,
  
  // Property Management Features
  property_types: false,
  
  rooms: false,
  guests: false,
  housekeeping: false,
  maintenance: false,
  staff: false,
  drivers: false,
  riders: false,
  trips: false,
  vehicles: false,
  complaints: false,
  cities: false,
  static_content: false,
  profile_settings: true,
  coupons: false,
  darkMode: true,
  multiLanguage: false,
  realTimeUpdates: false,
  mobileResponsive: true,
};

export const getFeatureConfig = (): FeatureConfig => {
  const configType = import.meta.env.VITE_FEATURE_CONFIG || 'default';
  
  switch (configType) {
    case 'hotel-booking':
      return hotelBookingFeatures;
    case 'hotel':
      return hotelFeatures;
    case 'restaurant':
      return restaurantFeatures;
    case 'ecommerce':
      return ecommerceFeatures;
    case 'minimal':
      return minimalFeatures;
    default:
      return defaultFeatures;
  }
};

export const isFeatureEnabled = (feature: keyof FeatureConfig): boolean => {
  const config = getFeatureConfig();
  return config[feature] || false;
};

export const getEnabledFeatures = (): string[] => {
  const config = getFeatureConfig();
  return Object.entries(config)
    .filter(([, enabled]) => enabled)
    .map(([feature]) => feature);
};

export const getDisabledFeatures = (): string[] => {
  const config = getFeatureConfig();
  return Object.entries(config)
    .filter(([, enabled]) => !enabled)
    .map(([feature]) => feature);
}; 